import React, { useState, useCallback } from "react";
import {
  ActionLeft,
  ActionRight,
  Table,
  TableAction,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "./table-style.jsx";
import { Typography } from "../../ppui/typography.jsx";
import ColumnVisibility from "./column-visibility.jsx";
import PaginationControls from "./pagination-controls.jsx";
import SearchBox from "./search-box.jsx";
import StatusFilter from "./status-filter.jsx";
import { Box, Flex } from "../../ppui/layout.jsx";

const GlobalTable = ({
  headers,
  rows,
  ActiveSort,
  actionLeft,
  actionRight,
  TableActionShow = "true",
  searchBox,
  stickyHeader,
  columnManagement,
  FetchDataFromRow,
  rowsPerPage,
  currentPage,
  totalPages,
  handleNextPage,
  handlePreviousPage,
  handleRowsPerPageChange,
  setSearchQuery,
  maxHeight=false,
}) => {
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortColumn, setSortColumn] = useState(headers[0]?.key);

  const [visibleColumns, setVisibleColumns] = useState(
    headers.reduce((acc, header) => {
      acc[header.key] = header.isVisible !== false ? header.isVisible : true;
      return acc;
    }, {})
  );

  // const uniqueStatuses = Array.from(new Set(rows?.map((row) => row?.status)));
  const uniqueStatuses = Array.from(new Set(Array.isArray(rows) ? rows.map(row => row?.status) : []));

  // Helper function to get nested object value
  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  };

  // Helper function to get sortable value based on column
  const getSortableValue = (row, header) => {
    if (header.getValue) {
      switch (header.key) {
        case "full_name":
          return `${row.first_name} ${row.last_name}`.toLowerCase();
        case "designation":
          return row.designation?.designation_name?.toLowerCase() || "";
        case "department":
          return row.department?.dept_name?.toLowerCase() || "";
        case "date_of_birth":
        case "join_date":
          return new Date(row[header.key]).getTime();
        case "gender":
          return row.gender?.toLowerCase() || "";
        default:
          // eslint-disable-next-line no-case-declarations
          const value = header.key.includes(".")
            ? getNestedValue(row, header.key)
            : row[header.key];
          return value?.toString().toLowerCase() || "";
      }
    }

    const value = header.key.includes(".")
      ? getNestedValue(row, header.key)
      : row[header.key];

    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value.toLowerCase();
    if (value instanceof Date) return value.getTime();
    return value;
  };

  const getSortedRows = () => {
    if (!sortColumn) return rows;

    const header = headers.find((h) => h.key === sortColumn);
    if (!header?.isShortable) return rows;

    return [...rows].sort((a, b) => {
      const valueA = getSortableValue(a, header);
      const valueB = getSortableValue(b, header);

      if (valueA === valueB) return 0;

      const comparison = valueA < valueB ? -1 : 1;
      return sortOrder === "asc" ? comparison : -comparison;
    });
  };

  const handleSort = (columnKey) => {
    const columnHeader = headers?.find((header) => header?.key === columnKey);
    if (columnHeader && columnHeader?.isShortable) {
      const newSortOrder =
        sortColumn === columnKey && sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newSortOrder);
      setSortColumn(columnKey);
    }
  };

  function debounce(func, delay) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
    }, 300),
    []
  );

  const handleCheckboxChange = (key) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAllChange = (e) => {
    const { checked } = e.target;
    const updatedVisibility = headers.reduce((acc, header) => {
      acc[header.key] = checked;
      return acc;
    }, {});
    setVisibleColumns(updatedVisibility);
  };

  const handleApply = (newVisibility) => {
    setVisibleColumns(newVisibility);
  };

  const handleStatusFilter = (status) => {
    return status;
  };

  const handleRowClick = (row) => {
    if (FetchDataFromRow) {
      FetchDataFromRow(row);
    }
  };

  const sortedRows = getSortedRows();

  return (
    <>
      {TableActionShow && (
        <TableAction>
          <ActionLeft>
            {ActiveSort && (
              <StatusFilter
                uniqueStatuses={uniqueStatuses}
                onStatusFilter={handleStatusFilter}
              />
            )}
            {actionLeft && { ...actionLeft }}
          </ActionLeft>
          <ActionRight>
            {searchBox && <SearchBox onSearch={handleSearch} />}
            {columnManagement && (
              <ColumnVisibility
                headers={headers}
                visibleColumns={visibleColumns}
                handleCheckboxChange={handleCheckboxChange}
                handleSelectAllChange={handleSelectAllChange}
                onApply={handleApply}
              />
            )}
            {actionRight && { ...actionRight }}
          </ActionRight>
        </TableAction>
      )}
      <TableContainer maxHeight={maxHeight}>
        <Table>
          <TableHead>
            <TableRow>
              {headers.map(
                (header) =>
                  visibleColumns[header.key] && (
                    <TableCell
                      key={header.key}
                      isHeader
                      align={header.align || "left"}
                      onClick={() => handleSort(header.key)}
                      style={{
                        cursor: header.isShortable ? "pointer" : "default",
                      }}
                      className={`${header.isColumnSticky && "columnSticky"} ${stickyHeader && "stickyHeader"}`}
                    >
                      {header.label}
                      {header.isShortable && (
                        <span className="sort-indicator">
                          {sortColumn === header.key
                            ? sortOrder === "asc"
                              ? " ↑"
                              : " ↓"
                            : " ↕"}
                        </span>
                      )}
                    </TableCell>
                  )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(sortedRows) && sortedRows.length > 0 && (
              sortedRows.map((row, index) => (
                <TableRow key={row.id || index}>
                  {headers.map(
                    (header) =>
                      visibleColumns[header.key] && (
                        <TableCell
                          key={header.key}
                          align={header.align || "left"}
                          className={`${header.isColumnSticky && "columnSticky"}`}
                        >
                          {header.isClickable ? (
                            <Box
                              color="secondary"
                              sx={{ cursor: "pointer" }}
                              variant="body2"
                              onClick={() => handleRowClick(row)}
                              mb="0"
                            >
                              {header.getValue
                                ? header.getValue(row, index)
                                : header.key.includes(".")
                                  ? getNestedValue(row, header.key)
                                  : row[header.key] || header.defaultValue}
                            </Box>
                          ) : header.getValue ? (
                            header.getValue(row, index)
                          ) : header.key.includes(".") ? (
                            getNestedValue(row, header.key)
                          ) : (
                            row[header.key] || header.defaultValue
                          )}
                        </TableCell>
                      )
                  )}
                </TableRow>
              ))
            )} 
            {/* (
              <TableRow>
                <TableCell
                  colSpan={headers.length}
                  style={{ 
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                  }}
                >
                  No Data Available
                </TableCell>
              </TableRow>
            ) */}
            
          </TableBody>
        </Table>
        {sortedRows.length === 0 &&
        <Flex justifyContent="center" p={20} 
          style={{ 
            position: "sticky",
            top: 0, 
            left:0,
            zIndex: 2,
          }}>
          <Typography variant="body2" mb={"0"}>
            No Data Available
          </Typography>
        </Flex>
        }
      </TableContainer>
      <PaginationControls
        rowsPerPage={rowsPerPage}
        handleRowsPerPageChange={handleRowsPerPageChange}
        currentPage={currentPage}
        totalPages={totalPages}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
      />
    </>
  );
};

export default GlobalTable;
